import { useEffect, useState } from 'react';
import { faAddressBook, faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/shared/FormField';
import type { MeProfile, UpdateMyProfilePayload } from '@/types/profile.types';
import { InfoRow, ProfileSectionCard } from './ProfileSectionCard';

export interface ContactSectionProps {
  me: MeProfile;
  saving: boolean;
  /** Field-level errors from the server (400), keyed by field. */
  serverErrors?: Record<string, string>;
  onSave: (payload: UpdateMyProfilePayload) => Promise<boolean>;
}

type Draft = {
  alternatePhone: string;
  alternateEmail: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
};

const fromMe = (me: MeProfile): Draft => ({
  alternatePhone: me.alternatePhone ?? '',
  alternateEmail: me.alternateEmail ?? '',
  address: me.address ?? '',
  city: me.city ?? '',
  state: me.state ?? '',
  country: me.country ?? '',
  postalCode: me.postalCode ?? '',
});

function validate(d: Draft): Partial<Record<keyof Draft, string>> {
  const e: Partial<Record<keyof Draft, string>> = {};
  if (d.alternatePhone && !/^[+0-9 ()-]*$/.test(d.alternatePhone))
    e.alternatePhone = 'Digits, spaces, +, ( ) and - only.';
  if (d.alternateEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.alternateEmail))
    e.alternateEmail = 'Enter a valid email address.';
  if (d.postalCode && !/^[A-Za-z0-9 -]*$/.test(d.postalCode))
    e.postalCode = 'Letters, digits, spaces or hyphens only.';
  return e;
}

/**
 * Contact + address: the self-editable set of P1 (spec §7.1, OD-1 default).
 * Rendered read-only when the server says it is not editable; the server is
 * the authority either way.
 */
export function ContactSection({ me, saving, serverErrors, onSave }: ContactSectionProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => fromMe(me));
  const [errors, setErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  useEffect(() => {
    if (!editing) setDraft(fromMe(me));
  }, [me, editing]);

  const canEdit = me.capabilities.canEditContact && me.capabilities.canEditAddress;
  const set = (k: keyof Draft, v: string) => setDraft((d) => ({ ...d, [k]: v }));
  const err = (k: keyof Draft) => errors[k] ?? serverErrors?.[k];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next = validate(draft);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const ok = await onSave({
      alternatePhone: draft.alternatePhone.trim() || null,
      alternateEmail: draft.alternateEmail.trim() || null,
      address: draft.address.trim() || null,
      city: draft.city.trim() || null,
      state: draft.state.trim() || null,
      country: draft.country.trim() || null,
      postalCode: draft.postalCode.trim() || null,
    });
    if (ok) setEditing(false);
  };

  return (
    <ProfileSectionCard
      title="Contact & address"
      description="How the service team can reach you outside work channels."
      icon={faAddressBook}
      testId="profile-contact"
      action={
        canEdit && !editing ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="min-h-11 sm:min-h-8"
            onClick={() => setEditing(true)}
          >
            <FontAwesomeIcon icon={faPencil} className="mr-1.5 h-3 w-3" />
            Edit
          </Button>
        ) : undefined
      }
    >
      {!editing ? (
        <div className="space-y-2">
          <InfoRow label="Alternate phone" value={me.alternatePhone} />
          <InfoRow label="Alternate email" value={me.alternateEmail} />
          <InfoRow label="Address" value={me.address} />
          <InfoRow label="City" value={me.city} />
          <InfoRow label="State" value={me.state} />
          <InfoRow label="Country" value={me.country} />
          <InfoRow label="Postal code" value={me.postalCode} />
        </div>
      ) : (
        <form noValidate onSubmit={(e) => void submit(e)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Alternate phone"
              htmlFor="profile-alternatePhone"
              error={err('alternatePhone')}
            >
              <Input
                id="profile-alternatePhone"
                value={draft.alternatePhone}
                onChange={(e) => set('alternatePhone', e.target.value)}
                aria-invalid={!!err('alternatePhone')}
                className="min-h-11 sm:min-h-10"
                inputMode="tel"
                maxLength={30}
              />
            </FormField>
            <FormField
              label="Alternate email"
              htmlFor="profile-alternateEmail"
              error={err('alternateEmail')}
            >
              <Input
                id="profile-alternateEmail"
                type="email"
                value={draft.alternateEmail}
                onChange={(e) => set('alternateEmail', e.target.value)}
                aria-invalid={!!err('alternateEmail')}
                className="min-h-11 sm:min-h-10"
                maxLength={255}
              />
            </FormField>
            <FormField
              label="Address"
              htmlFor="profile-address"
              className="sm:col-span-2"
              error={err('address')}
            >
              <Input
                id="profile-address"
                value={draft.address}
                onChange={(e) => set('address', e.target.value)}
                className="min-h-11 sm:min-h-10"
                maxLength={500}
              />
            </FormField>
            <FormField label="City" htmlFor="profile-city" error={err('city')}>
              <Input
                id="profile-city"
                value={draft.city}
                onChange={(e) => set('city', e.target.value)}
                className="min-h-11 sm:min-h-10"
                maxLength={100}
              />
            </FormField>
            <FormField label="State" htmlFor="profile-state" error={err('state')}>
              <Input
                id="profile-state"
                value={draft.state}
                onChange={(e) => set('state', e.target.value)}
                className="min-h-11 sm:min-h-10"
                maxLength={100}
              />
            </FormField>
            <FormField label="Country" htmlFor="profile-country" error={err('country')}>
              <Input
                id="profile-country"
                value={draft.country}
                onChange={(e) => set('country', e.target.value)}
                className="min-h-11 sm:min-h-10"
                maxLength={100}
              />
            </FormField>
            <FormField label="Postal code" htmlFor="profile-postalCode" error={err('postalCode')}>
              <Input
                id="profile-postalCode"
                value={draft.postalCode}
                onChange={(e) => set('postalCode', e.target.value)}
                aria-invalid={!!err('postalCode')}
                className="min-h-11 sm:min-h-10"
                maxLength={20}
              />
            </FormField>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="secondary"
              className="min-h-11 sm:min-h-9"
              disabled={saving}
              onClick={() => {
                setErrors({});
                setDraft(fromMe(me));
                setEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" className="min-h-11 sm:min-h-9" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      )}
    </ProfileSectionCard>
  );
}
